import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAVEABLE_ROUTES = {
  '/work-orders': 'Work Orders',
  '/assets': 'Assets',
  '/inspections': 'Inspections',
  '/operations': 'Operations',
  '/vendors': 'Vendors',
  '/ppm': 'Preventive Maintenance',
  '/budget': 'Budget',
};
const routeLabel = (p) => SAVEABLE_ROUTES[p] || p;

/**
 * All saved-view orchestration handlers. Returned callbacks are stable across
 * renders (wrapped in useCallback) so SavedViewRow can be a clean React.memo
 * candidate.
 *
 * The hook owns no state — it operates on the view list / dialog targets via
 * the setters passed in by the parent. This keeps the component tree in
 * charge of UI state, while the hook owns side effects (HTTP, toasts).
 */
export const useSavedViewActions = ({
  authHeaders,
  navigate,
  setViews,
  setOpen,
  setEditView,
  setSchedView,
}) => {
  const bumpAccess = useCallback((viewId) => {
    setViews((prev) => prev.map((v) => v.id === viewId
      ? { ...v, access_count: (v.access_count || 0) + 1, last_accessed: new Date().toISOString() }
      : v));
    axios
      .post(`${API_URL}/api/saved-views/${viewId}/access`, {}, { headers: authHeaders() })
      .catch((err) => logger.debug('access bump failed', err));
  }, [authHeaders, setViews]);

  const applyView = useCallback((view) => {
    navigate(`${view.path}${view.query_string ? `?${view.query_string}` : ''}`);
    setOpen(false);
    bumpAccess(view.id);
  }, [navigate, setOpen, bumpAccess]);

  const copyLink = useCallback((view) => {
    navigator.clipboard.writeText(
      `${window.location.origin}${view.path}${view.query_string ? `?${view.query_string}` : ''}`
    );
    toast.success('Link copied');
  }, []);

  const openInNewTab = useCallback((view) => {
    window.open(
      `${view.path}${view.query_string ? `?${view.query_string}` : ''}`,
      '_blank',
      'noopener,noreferrer'
    );
    bumpAccess(view.id);
  }, [bumpAccess]);

  const deleteView = useCallback(async (view) => {
    if (!view.is_owner) return;
    try {
      await axios.delete(`${API_URL}/api/saved-views/${view.id}`, { headers: authHeaders() });
      setViews((prev) => prev.filter((v) => v.id !== view.id));
      toast.success('View deleted');
    } catch (err) {
      logger.error('delete view failed', err);
      toast.error('Failed to delete view');
    }
  }, [authHeaders, setViews]);

  const toggleSubscribe = useCallback(async (view) => {
    try {
      if (view.is_subscribed) {
        await axios.delete(`${API_URL}/api/saved-views/${view.id}/subscribe`, { headers: authHeaders() });
        toast.success(`Unsubscribed from “${view.name}”`);
      } else {
        await axios.post(`${API_URL}/api/saved-views/${view.id}/subscribe`, {}, { headers: authHeaders() });
        toast.success(`Subscribed to “${view.name}” — you'll be notified when items match`);
      }
      setViews((prev) => prev.map((v) => v.id === view.id ? { ...v, is_subscribed: !v.is_subscribed } : v));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subscription failed');
    }
  }, [authHeaders, setViews]);

  // P0.2 — Per-view subscription email opt-in
  const toggleSubscriptionEmail = useCallback(async (view) => {
    const next = !view.subscription_email_optin;
    try {
      await axios.put(
        `${API_URL}/api/saved-views/${view.id}/subscription-email`,
        { enabled: next },
        { headers: authHeaders() }
      );
      setViews((prev) => prev.map((v) => v.id === view.id ? { ...v, subscription_email_optin: next } : v));
      toast.success(next ? 'Will email you when matches arrive' : 'Email digest disabled');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not update email opt-in'); }
  }, [authHeaders, setViews]);

  // P1.7 — Per-user favourites star
  const toggleFavourite = useCallback(async (view) => {
    try {
      const { data } = await axios.put(
        `${API_URL}/api/saved-views/${view.id}/favourite`, {}, { headers: authHeaders() }
      );
      setViews((prev) => prev.map((v) => v.id === view.id ? { ...v, is_favourited: data.favourited } : v));
    } catch (err) { toast.error(err.response?.data?.detail || 'Favourite failed'); }
  }, [authHeaders, setViews]);

  // P0.3 — Team-wide default for shared views
  const setTeamDefault = useCallback(async (view) => {
    try {
      await axios.put(
        `${API_URL}/api/saved-views/${view.id}/team-default`, {}, { headers: authHeaders() }
      );
      toast.success(`Team default set for ${view.path}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not set team default'); }
  }, [authHeaders]);

  const setAsDefault = useCallback(async (view) => {
    try {
      await axios.put(`${API_URL}/api/saved-views/${view.id}/set-default`, {}, { headers: authHeaders() });
      toast.success(`Default view set for ${routeLabel(view.path)}`);
      setViews((prev) => prev.map((v) => ({
        ...v,
        is_default_for_path: v.path === view.path ? v.id === view.id : v.is_default_for_path,
      })));
    } catch {
      toast.error('Could not set default');
    }
  }, [authHeaders, setViews]);

  const clearDefault = useCallback(async (view) => {
    try {
      await axios.delete(
        `${API_URL}/api/saved-views-defaults?path=${encodeURIComponent(view.path)}`,
        { headers: authHeaders() }
      );
      setViews((prev) => prev.map((v) => v.path === view.path ? { ...v, is_default_for_path: false } : v));
      toast.success(`Default cleared for ${routeLabel(view.path)}`);
    } catch {
      toast.error('Could not clear default');
    }
  }, [authHeaders, setViews]);

  const openEdit = useCallback((v) => setEditView(v), [setEditView]);
  const openSchedule = useCallback((v) => setSchedView(v), [setSchedView]);

  const submitEdit = useCallback(async (target, payload) => {
    if (!target) return;
    try {
      const { data } = await axios.put(
        `${API_URL}/api/saved-views/${target.id}`,
        payload,
        { headers: authHeaders() },
      );
      setViews((prev) => prev.map((v) => v.id === data.id ? { ...v, ...data, is_owner: true } : v));
      toast.success('View updated');
      setEditView(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  }, [authHeaders, setViews, setEditView]);

  const submitSchedule = useCallback(async (target, { enabled, frequency }) => {
    if (!target) return;
    try {
      const { data } = await axios.put(
        `${API_URL}/api/saved-views/${target.id}/schedule`,
        { enabled, frequency },
        { headers: authHeaders() },
      );
      setViews((prev) => prev.map((v) => v.id === data.id ? { ...v, ...data, is_owner: true } : v));
      toast.success(enabled ? `Scheduled — emailed ${frequency}` : 'Schedule cleared');
      setSchedView(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Schedule failed');
    }
  }, [authHeaders, setViews, setSchedView]);

  // P0 — column-aware downloads (CSV + PDF). When `columns` is omitted the
  // backend falls back to its default per-collection list.
  const downloadView = useCallback(async (view, format, columns = '') => {
    try {
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const url = `${API_URL}/api/saved-views/${view.id}/export.${ext}`
        + (columns ? `?columns=${encodeURIComponent(columns)}` : '');
      const res = await axios.get(url, {
        headers: authHeaders(), responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = blobUrl;
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `${view.name || 'view'}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Exported “${view.name}” as ${ext.toUpperCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  }, [authHeaders]);

  const exportCsv = useCallback((view, columns = '') => downloadView(view, 'csv', columns), [downloadView]);
  const exportPdf = useCallback((view, columns = '') => downloadView(view, 'pdf', columns), [downloadView]);

  // P1.6 — Pin to Dashboard
  const togglePin = useCallback(async (view) => {
    try {
      const { data } = await axios.put(
        `${API_URL}/api/saved-views/${view.id}/pin`, {}, { headers: authHeaders() }
      );
      setViews((prev) => prev.map((v) => v.id === view.id ? { ...v, is_pinned: data.pinned } : v));
      toast.success(data.pinned ? 'Pinned to Dashboard' : 'Unpinned from Dashboard');
    } catch (err) { toast.error(err.response?.data?.detail || 'Pin failed'); }
  }, [authHeaders, setViews]);

  // P1.4 — Move up / down (uses manual_order)
  const moveOrder = useCallback(async (view, delta) => {
    const current = typeof view.manual_order === 'number' ? view.manual_order : 100;
    const next = Math.max(0, current + delta);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/saved-views/${view.id}`,
        { manual_order: next },
        { headers: authHeaders() }
      );
      setViews((prev) => prev.map((v) => v.id === data.id ? { ...v, ...data, is_owner: true } : v));
    } catch (err) { toast.error(err.response?.data?.detail || 'Reorder failed'); }
  }, [authHeaders, setViews]);

  // P1.4 (drag-and-drop variant) — silent persist on each affected row
  const reorderView = useCallback(async ({ id, manual_order }) => {
    try {
      const { data } = await axios.put(
        `${API_URL}/api/saved-views/${id}`,
        { manual_order },
        { headers: authHeaders() }
      );
      setViews((prev) => prev.map((v) => v.id === data.id ? { ...v, ...data, is_owner: true } : v));
    } catch (err) { logger.debug('reorder failed', err); }
  }, [authHeaders, setViews]);

  // P1.5 — Permalink slug
  const setSlug = useCallback(async (view) => {
    const desired = window.prompt(
      'Permalink slug (lowercase letters, numbers, hyphens). Leave blank to auto-derive from name.',
      view.slug || ''
    );
    if (desired === null) return; // cancelled
    try {
      const { data } = await axios.post(
        `${API_URL}/api/saved-views/${view.id}/slug`,
        { slug: desired },
        { headers: authHeaders() }
      );
      const fullUrl = `${window.location.origin}/v/${data.slug}`;
      navigator.clipboard.writeText(fullUrl).catch(() => {});
      setViews((prev) => prev.map((v) => v.id === view.id ? { ...v, slug: data.slug } : v));
      toast.success(`Permalink ${fullUrl} copied`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set permalink');
    }
  }, [authHeaders, setViews]);

  return {
    bumpAccess,
    applyView,
    copyLink,
    openInNewTab,
    deleteView,
    toggleSubscribe,
    setAsDefault,
    clearDefault,
    openEdit,
    openSchedule,
    submitEdit,
    submitSchedule,
    exportCsv,
    exportPdf,
    togglePin,
    toggleFavourite,
    toggleSubscriptionEmail,
    setTeamDefault,
    moveOrder,
    reorderView,
    setSlug,
  };
};
